import React, {useEffect, useState, useContext} from 'react';
import withAppContext from "tbk-components/src/hooks/withAppContext";
import {ICareersLocation} from "./CareersLocationHero";
import CareerCardGroup, {ICareerCardGroupProps, ICareersFilterProps} from "../CareerCardGroup";
import { ICareerCardProps } from '../CareerCard';
import Spacer from '../../common/Spacer';

export interface ICareersLocationFooterProps {
	/**
	 * This is meant to be something like "All Careers in Saskatoon".  Optionally, use the placeholder %1 for the location name.
	 * For example, "All Careers in %1".
	 */
	heading?: string;
	/**
	 * Select the location to display the footer for.  This will be used to replace placeholders and filter what careers.
	 * are displayed.
	 */
	location?: ICareersLocation[];
	/**
	 * @noUI
	 */
	filters?: ICareersFilterProps[];
	/**
	 * This is meant to display something like "16 Careers in 3 Categories".  Use the placeholder %1 for the
	 * number of careers and %2 for the number of categories.  We will fill those in automatically based on the
	 * location you've select.  For example, "%1 Careers in %2 Categories".
	 *
	 * Note, we are removing this and filling it in automatically server side to accommodate singular/plural variations.
	 *
	 * @noUI
	 */
	tagline?: string;
	/**
	 * The text for the "Load More" button. ( We'll fill this in automatically on server side, thus noUI )
	 *
	 * @noUI
	 */
	loadMoreText?: string;
	/**
	 * The text that shows when the user has filtered the results.
	 *
	 * @noUI
	 */
	intermediateResultsText?: [string, string];
	/**
	 * The text that shows when there are no results.
	 *
	 * @noUI
	 */
	noResultsText?: string;
	/**
	 * @noUI
	 */
	defaultCareers?: {
		total: number,
		cards: ICareerCardProps[]
	}
	/**
	 * Using this to show messaging only in the admin context.
	 *
	 * @noUI
	 */
	isAdmin?: boolean;
}

/**
 * This component is meant to display on the bottom of a location landing page.  It will render a list of jobs.
 *
 * @block
 * @icon feedback
 */
const CareersLocationFooter: React.FC<ICareersLocationFooterProps> = withAppContext(({
	location,
	heading = 'All Careers in %1',
	tagline = '%1 Careers in %2 Categories',
	filters = [
		{
			taxonomy: 'location',
			label: 'Location',
			options: [
				{
					label: 'This list gets',
					value: '1',
					children: [
						{
							label: 'filled in automatically',
							value: '2',
						},
						{
							label: 'with correct data',
							value: '3',
						},
						{
							label: 'on the front end',
							value: '4',
						}
					]
				},
			]
		},
		{
			taxonomy: 'job_type',
			label: 'Job Type',
			options: [
				{
					label: 'This list gets',
					value: '1',
					children: [
						{
							label: 'filled in automatically',
							value: '2',
						},
						{
							label: 'with correct data',
							value: '3',
						},
						{
							label: 'on the front end',
							value: '4',
						}
					]
				},
			]
		},
	] as ICareersFilterProps[],
	loadMoreText = 'Load More',
	intermediateResultsText = ['Showing %1 of %2 job','Showing %1 of %2 jobs'],
	noResultsText = 'No results found',
	defaultCareers,
	isAdmin = true,
	context
}) => {
	const {Api} = useContext( context );
	const [careers,setCareers] = useState<{total:number,cards:ICareerCardProps[]}>(null);
	const [busy,setBusy] = useState<boolean>(false);
	const [filledTagline, setFilledTagline] = useState<string>( null );
	const [filledHeading, setFilledHeading] = useState<string>( null );

	// Doing it like this prevents the effect from being called on every render because the location is always a new object.
	// The Array.isArray is temporary backwards compatibility.
	// There's been some back and forth on whether location is to be a single or an array.  We're going to to accommodate both.
	let locations = {}; // key is taxonomy, value is array of slugs
	if ( location ) {
		if ( Array.isArray( location ) ) {
			// This is when there are multiple
			location.forEach( l => {
				if ( l.slug ) {
					if ( ! locations[ l.taxonomy ] ) {
						locations[ l.taxonomy ] = [];
					}
					locations[ l.taxonomy ].push( l.slug );
				}
			});
		} else {
			const l = location as ICareersLocation;
			if ( l.slug ) {
				locations[ l.taxonomy ] = l.slug;
			}
		}
	}

	const location_slug = Object.entries( locations ).length ? JSON.stringify(locations) : null;
	useEffect( () => {
		if ( defaultCareers ) {
			setCareers( defaultCareers );
		} else if ( location_slug ) {
			setBusy(true);
			Api.getFilteredJobs({...JSON.parse(location_slug),all:1}).then( results => {
				setCareers( results );
			}).finally(() => {
				setBusy(false);
			})
		}

	},[defaultCareers,location_slug]);

	useEffect(() => {
		if ( ! location ) {
			return;
		}
		const data = {} as ICareersLocation;
		const names = [];
		( Array.isArray( location ) ? location : [location as ICareersLocation] ).forEach( l => {
			['numberOfCareers','numberOfCategories'].forEach( key => {
				if ( ! data.hasOwnProperty( key ) ) {
					data[ key ] = 0;
				}
				data[ key ] += parseInt( l[ key ], 10 );
			});
			names.push( l.name );
		});
		const fillTagline = (data:ICareersLocation) => {
			setFilledTagline( ( tagline || '' )
				.replace('%1', `<strong>${data.numberOfCareers}</strong>`)
				.replace('%2', `<strong>${data.numberOfCategories}</strong>`) +
				( isAdmin ? '<div class="text-muted fs-9">Note: these numbers are placeholders only.  Proper numbers appear on the front end.</div>' : '')
			);
		};
		setFilledHeading( ( heading || '' ).replace('%1', names.join(' & ')) );
		if ( data.hasOwnProperty('numberOfCareers') && data.hasOwnProperty('numberOfCategories') ) {
			fillTagline( data );
		}
	}, [location,tagline,heading,isAdmin] );

	if ( ! location_slug && ! defaultCareers ) {
		// If no careers, and no location slug to query careers, then show nothing.
		return isAdmin ? <div className="text-center">Select a location to see a preview here.</div> : null;
	}

	const careerCardGroupProps = {
		heading: filledHeading,
		cardSummaryHeading: filledTagline,
		calianCareerCards: careers?.cards,
		showResults: !!filledTagline, // needs to be a boolean
		showFilter: filters?.length > 0,
		filters: filters || [],
		loadMoreButtonText: loadMoreText,
		intermediateResultsText,
		noResultsText,
		busy
	} as ICareerCardGroupProps

	return <div>
		<CareerCardGroup {...careerCardGroupProps} />
		<Spacer size="Large on Desktop" />
	</div>;
});

export default CareersLocationFooter;
